/**
 * In-app browser detection.
 *
 * WHY THIS MATTERS ON THE PAYMENT PAGE
 * A client opened an invoice link from a message on 2026-08-07 and the page
 * threw `SecurityError: Blocked a frame with origin ... from accessing a
 * cross-origin frame`. That error itself is harmless — it comes from Stripe.js's
 * hidden iframes being probed by the host app, not from our code, which never
 * touches window.frames, window.top or contentWindow anywhere.
 *
 * The environment is the actual problem. An in-app WKWebView (Messages, Gmail,
 * Instagram, Facebook, LinkedIn) is a poor place to take a card payment:
 *   - storage is partitioned, so Stripe's session can be lost on redirect
 *   - Apple Pay and Google Pay are usually unavailable
 *   - some hosts silently drop the return URL, so a paid invoice never shows
 *     as paid to the client
 *   - saved cards and autofill from the real browser are not available
 *
 * None of that throws an error you can catch. It just converts worse. So the
 * page tells the client how to escape the webview instead of hoping.
 *
 * DETECTION RULE
 * Real Safari on iOS always carries a `Safari/` token, as do Chrome (`CriOS`)
 * and Firefox (`FxiOS`) because they are Safari under the skin. An embedded
 * WKWebView carries neither `Safari/` nor `Version/`. Android in-app webviews
 * carry `; wv`. Everything else is matched by explicit host tokens.
 *
 * Deliberately conservative: a false positive nags someone who is browsing
 * normally, which is worse than the problem. When unsure, return false.
 */

/** Host apps that identify themselves outright. */
const KNOWN_HOSTS = [
  'FBAN', 'FBAV', 'FB_IAB',   // Facebook
  'Instagram',
  'LinkedInApp',
  'Twitter',
  'Line/',
  'MicroMessenger',           // WeChat
  'Snapchat',
  'Pinterest',
  'GSA/'                      // Google app
];

/**
 * @param {string} [ua] - injectable for tests
 * @returns {boolean}
 */
export function isInAppBrowser(ua) {
  const s = String(ua !== undefined ? ua : (typeof navigator !== 'undefined' ? navigator.userAgent : ''));
  if (!s) return false;

  if (KNOWN_HOSTS.some(h => s.includes(h))) return true;

  // Android embedded webview.
  if (/\bwv\b/.test(s) || /Android.*Version\/[\d.]+.*Chrome/.test(s) === false && /; wv\)/.test(s)) {
    if (/; wv\)/.test(s) || /\bwv\b/.test(s)) return true;
  }

  // iOS: a genuine browser always reports a Safari/ token.
  if (/(iPhone|iPad|iPod)/.test(s)) {
    if (/CriOS\//.test(s) || /FxiOS\//.test(s) || /EdgiOS\//.test(s)) return false;
    return !/Safari\//.test(s);
  }

  return false;
}

/** Human name for the host app, when it says who it is. */
export function inAppBrowserName(ua) {
  const s = String(ua !== undefined ? ua : (typeof navigator !== 'undefined' ? navigator.userAgent : ''));
  if (/FBAN|FBAV|FB_IAB/.test(s)) return 'Facebook';
  if (/Instagram/.test(s)) return 'Instagram';
  if (/LinkedInApp/.test(s)) return 'LinkedIn';
  if (/MicroMessenger/.test(s)) return 'WeChat';
  if (/Snapchat/.test(s)) return 'Snapchat';
  if (/GSA\//.test(s)) return 'the Google app';
  return null;
}

/** Platform-appropriate escape instructions. */
export function escapeInstructions(ua) {
  const s = String(ua !== undefined ? ua : (typeof navigator !== 'undefined' ? navigator.userAgent : ''));
  if (/(iPhone|iPad|iPod)/.test(s)) {
    return 'Tap the \u22ef or share icon, then "Open in Safari".';
  }
  if (/Android/.test(s)) {
    return 'Tap the \u22ee menu, then "Open in Chrome" or "Open in browser".';
  }
  return 'Open this link in your normal web browser.';
}

export default isInAppBrowser;
